import { FeatureLabel } from '@/components/marketing/info-tip';
import { useState } from 'react';
import { Check, X, Minus, ChevronDown, Clock, Sparkles } from 'lucide-react';
import type { CompetitorData, FeatureValue } from '@/routes/compare/data';

interface FeatureMatrixProps {
  data: CompetitorData;
}

function ValueCell({ value }: { value: FeatureValue }) {
  if (value === true)
    return (
      <div className="flex items-center justify-center gap-1.5">
        <div className="size-5 rounded-full bg-green-50 flex items-center justify-center">
          <Check className="size-3 text-green-600" />
        </div>
      </div>
    );
  if (value === false)
    return (
      <div className="flex items-center justify-center">
        <div className="size-5 rounded-full bg-zinc-100 flex items-center justify-center">
          <X className="size-3 text-zinc-400" />
        </div>
      </div>
    );
  if (value === '~')
    return (
      <div className="flex items-center justify-center">
        <div className="size-5 rounded-full bg-amber-50 flex items-center justify-center">
          <Minus className="size-3 text-amber-600" />
        </div>
      </div>
    );

  // String values — check for special keywords
  const str = String(value);
  const isComingSoon = /coming soon|planned/i.test(str);
  const isFree = str.toLowerCase().includes('free');
  const isUnlimited = str.toLowerCase().includes('unlimited');

  if (isComingSoon) {
    return (
      <div className="flex items-center justify-center gap-1.5">
        <Clock className="size-3 text-amber-500 shrink-0" />
        <span className="text-xs text-amber-600 font-medium">{str}</span>
      </div>
    );
  }
  if (isFree || isUnlimited) {
    return (
      <div className="flex items-center justify-center gap-1">
        <Sparkles className="size-3 text-green-500 shrink-0" />
        <span className="text-xs text-green-600 font-medium">{str}</span>
      </div>
    );
  }
  return <span className="text-xs text-zinc-600 text-center block">{str}</span>;
}

export function FeatureMatrix({ data }: FeatureMatrixProps) {
  const [differencesOnly, setDifferencesOnly] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggleCategory = (cat: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const isDifferent = (kora: FeatureValue, comp: FeatureValue) => {
    return String(kora) !== String(comp);
  };

  return (
    <section className="py-16 md:py-20 border-t border-zinc-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-zinc-900 mb-1">
              Feature comparison
            </h2>
            <p className="text-sm text-zinc-500">
              Honest, side-by-side. Where we differ, we say so.
            </p>
          </div>
          <button
            onClick={() => setDifferencesOnly(!differencesOnly)}
            className="inline-flex items-center gap-2 text-xs font-medium text-zinc-500 hover:text-zinc-900 transition-colors"
          >
            <div className={`size-4 rounded border flex items-center justify-center transition-colors ${differencesOnly
              ? 'bg-zinc-900 border-zinc-900'
              : 'matrix-check'
              }`}>
              {differencesOnly && <Check className="size-3 text-white" />}
            </div>
            Show differences only
          </button>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-zinc-400">
          <div className="flex items-center gap-1.5">
            <div className="size-4 rounded-full bg-green-50 flex items-center justify-center">
              <Check className="size-2.5 text-green-600" />
            </div>
            Included
          </div>
          <div className="flex items-center gap-1.5">
            <Sparkles className="size-3 text-green-500" /> Free / unlimited
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="size-3 text-amber-500" /> Planned (not available yet)
          </div>
          <div className="flex items-center gap-1.5">
            <Minus className="size-3 text-amber-500" /> Partial / limited
          </div>
          <div className="flex items-center gap-1.5">
            <X className="size-3 text-zinc-400" /> Not available
          </div>
        </div>

        {/* Matrix */}
        <div className="rounded-[22px] border border-zinc-200 overflow-clip bg-white">
          {/* Sticky header */}
          <div className="sticky top-16 z-10 bg-white border-b border-zinc-200">
            <div className="grid grid-cols-12 gap-2 px-4 py-3.5">
              <div className="col-span-4 sm:col-span-6 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Feature
              </div>
              <div className="col-span-4 sm:col-span-3 text-center">
                <div className="flex items-center justify-center gap-1.5">
                  <img width={14} height={14} src="/assets/logo_square.svg" alt="Kora logo" className="size-3.5" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-zinc-900">Kora</span>
                </div>
              </div>
              <div className="col-span-4 sm:col-span-3 text-center">
                <div className="flex items-center justify-center gap-1.5">
                  <div className="size-3.5 rounded bg-zinc-200 flex items-center justify-center text-[7px] font-bold text-zinc-600">
                    {data.name[0]}
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wide text-zinc-700 truncate">
                    {data.name}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Categories */}
          {data.features.map((cat) => {
            const visibleRows = differencesOnly
              ? cat.rows.filter((r) => isDifferent(r.kora, r.competitor))
              : cat.rows;
            if (visibleRows.length === 0) return null;

            const isCollapsed = collapsed.has(cat.category);

            return (
              <div key={cat.category} className="border-b border-zinc-100 last:border-0">
                <button
                  onClick={() => toggleCategory(cat.category)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-zinc-50 hover:bg-zinc-100 transition-colors"
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-zinc-700">
                    {cat.category}
                  </span>
                  <ChevronDown
                    className={`size-4 text-zinc-400 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
                  />
                </button>
                {!isCollapsed && (
                  <div>
                    {visibleRows.map((row, i) => (
                      <div
                        key={row.label}
                        className={`grid grid-cols-12 gap-2 px-4 py-3 items-center border-b border-zinc-50 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'matrix-stripe'
                          } transition-colors`}
                      >
                        <div className="col-span-4 sm:col-span-6 text-sm text-zinc-700">
                          <FeatureLabel label={row.label} />
                        </div>
                        <div className="col-span-4 sm:col-span-3">
                          <ValueCell value={row.kora} />
                        </div>
                        <div className="col-span-4 sm:col-span-3">
                          <ValueCell value={row.competitor} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
